import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Eye, Mail } from 'lucide-react'
import { formatDistance } from 'date-fns'
import type { ArtistWithDetails } from '../types'

interface ArtistTableProps {
  artists: ArtistWithDetails[]
  selectedIds: number[]
  onSelectionChange: (ids: number[]) => void
  onViewArtist: (artist: ArtistWithDetails) => void
  onSendEmail: (artist: ArtistWithDetails) => void
}

export function ArtistTable({
  artists,
  selectedIds,
  onSelectionChange,
  onViewArtist,
  onSendEmail,
}: ArtistTableProps) {
  const allSelected = artists.length > 0 && selectedIds.length === artists.length

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange([])
    } else {
      onSelectionChange(artists.map((a) => a.id))
    }
  }

  const toggleOne = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id))
    } else {
      onSelectionChange([...selectedIds, id])
    }
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
            </TableHead>
            <TableHead>Artist</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Artworks</TableHead>
            <TableHead>Last Active</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {artists.map((artist) => (
            <TableRow key={artist.id}>
              <TableCell>
                <Checkbox
                  checked={selectedIds.includes(artist.id)}
                  onCheckedChange={() => toggleOne(artist.id)}
                />
              </TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">{artist.artistName || `${artist.firstName} ${artist.lastName}`}</p>
                  <p className="text-sm text-muted-foreground">
                    {artist.firstName} {artist.lastName}
                  </p>
                </div>
              </TableCell>
              <TableCell className="text-sm">{artist.email}</TableCell>
              <TableCell>
                {artist.verified ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Verified
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    Unverified
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-1 text-sm">
                  <span className="text-muted-foreground">{artist.artworksDraftCount}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-yellow-600">{artist.artworksPendingCount}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-green-600">{artist.artworksPublishedCount}</span>
                </div>
                <p className="text-xs text-muted-foreground">draft/pending/published</p>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {artist.dateLastLogin
                  ? formatDistance(new Date(artist.dateLastLogin), new Date(), { addSuffix: true })
                  : 'Never'}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewArtist(artist)}
                    title="View artist"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onSendEmail(artist)}
                    title="Send email"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {artists.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No artists found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
